import * as THREE from 'three';

export default class Portal {
    constructor(scene, position, color, isStart = false) {
        this.scene = scene;
        this.isStart = isStart;
        
        //console.log(`Creating ${isStart ? 'start' : 'exit'} portal at position:`, position);
        
        // Create portal group
        this.portalGroup = new THREE.Group();
        this.portalGroup.position.copy(position);
        this.portalGroup.rotation.x = 0.35;
        this.portalGroup.rotation.y = 0;
        
        // Create portal effect (torus)
        const portalGeometry = new THREE.TorusGeometry(15, 2, 16, 100);
        const portalMaterial = new THREE.MeshPhongMaterial({
            color: color,
            emissive: color,
            transparent: true,
            opacity: 0.8
        });
        this.portal = new THREE.Mesh(portalGeometry, portalMaterial);
        this.portalGroup.add(this.portal);
        
        // Create portal inner surface
        const portalInnerGeometry = new THREE.CircleGeometry(13, 32);
        const portalInnerMaterial = new THREE.MeshBasicMaterial({
            color: color,
            transparent: true,
            opacity: 0.5,
            side: THREE.DoubleSide
        });
        this.portalInner = new THREE.Mesh(portalInnerGeometry, portalInnerMaterial);
        this.portalGroup.add(this.portalInner);
        
        // Create particle system
        this.setupParticleSystem(color);
        
        // Add portal group to scene
        scene.add(this.portalGroup);
        
        // Create collision box
        this.collisionBox = new THREE.Box3().setFromObject(this.portalGroup);
        
        // Start animation
        this.animate();
    }
    
    setupParticleSystem(color) {
        const particleCount = 1000;
        const particles = new THREE.BufferGeometry();
        const positions = new Float32Array(particleCount * 3);
        const colors = new Float32Array(particleCount * 3);
        
        for (let i = 0; i < particleCount * 3; i += 3) {
            // Create particles in a ring around the portal
            const angle = Math.random() * Math.PI * 2;
            const radius = 15 + (Math.random() - 0.5) * 4;
            positions[i] = Math.cos(angle) * radius;
            positions[i + 1] = Math.sin(angle) * radius;
            positions[i + 2] = (Math.random() - 0.5) * 4;
            
            // Set color with slight variation
            if (color === 0xff0000) { // Red portal
                colors[i] = 0.8 + Math.random() * 0.2;
                colors[i + 1] = 0;
                colors[i + 2] = 0;
            } else { // Green portal
                colors[i] = 0;
                colors[i + 1] = 0.8 + Math.random() * 0.2;
                colors[i + 2] = 0;
            }
        }
        
        particles.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        particles.setAttribute('color', new THREE.BufferAttribute(colors, 3));
        
        const particleMaterial = new THREE.PointsMaterial({
            size: 0.2,
            vertexColors: true,
            transparent: true,
            opacity: 0.6
        });
        
        this.particleSystem = new THREE.Points(particles, particleMaterial);
        this.portalGroup.add(this.particleSystem);
    }
    
    animate() {
        const positions = this.particleSystem.geometry.attributes.position.array;
        for (let i = 0; i < positions.length; i += 3) {
            positions[i + 1] += 0.05 * Math.sin(Date.now() * 0.001 + i);
        }
        this.particleSystem.geometry.attributes.position.needsUpdate = true;
        
        // Update portal shader time if needed
        if (this.portalInner.material.uniforms && this.portalInner.material.uniforms.time) {
            this.portalInner.material.uniforms.time.value = Date.now() * 0.001;
        }
        
        requestAnimationFrame(() => this.animate());
    }
    
    checkCollision(object) {
        if (!object) {
            //console.warn('Portal: No object passed to checkCollision');
            return false;
        }

        // For complex models, check if it's a Group or has children
        if (object instanceof THREE.Group) {
            // Use the first mesh found in the group for collision
            const mesh = object.children.find(child => child instanceof THREE.Mesh);
            if (!mesh) {
                //console.warn('Portal: No valid mesh found in group');
                return false;
            }
            object = mesh;
        }

        try {
            // Ensure object has valid geometry for bounding box
            if (!object.geometry && !object.children) {
                //console.warn('Portal: Object has no geometry or children');
                return false;
            }

            // For models with children but no direct geometry
            if (!object.geometry && object.children) {
                object = object.children[0];
            }
        
            // Update portal collision box
            this.collisionBox.setFromObject(this.portalGroup);
            
            const objectBox = new THREE.Box3().setFromObject(object);
            const hasCollision = this.collisionBox.intersectsBox(objectBox);
            
            /** Debug logging
           console.log('Portal Box:', {
                min: this.collisionBox.min.toArray(),
                max: this.collisionBox.max.toArray()
            });
            console.log('Object Box:', {
                min: objectBox.min.toArray(),
                max: objectBox.max.toArray()
            });
            */
            
            if (hasCollision) {
                //console.log(`Portal collision detected! Distance to object:`, 
                    //this.portalGroup.position.distanceTo(object.position));
            }
            
            return hasCollision;
        } catch (error) {
            //console.error('Portal: Error during collision check:', error);
            return false;
        }
    }
    
    dispose() {
        // Clean up resources
        this.portal.geometry.dispose();
        this.portal.material.dispose();
        this.portalInner.geometry.dispose();
        this.portalInner.material.dispose();
        this.particleSystem.geometry.dispose();
        this.particleSystem.material.dispose();
        this.scene.remove(this.portalGroup);
    }
}